import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Alert, Badge } from "@/components/ui/Feedback";
import { CheckboxField } from "@/components/ui/Field";
import { formatBytes, formatDateTime } from "@/lib/format";
import { importsApi, type ImportsOverview } from "@/lib/imports-api";

import { importErrorText } from "./errors";

function ImportDialog({ file, onClose }: { file: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dryRun, setDryRun] = useState(false);
  const [attachments, setAttachments] = useState(false);
  const [inlineImages, setInlineImages] = useState(true);
  const [repairText, setRepairText] = useState(true);
  const start = useMutation({
    mutationFn: () =>
      importsApi.startImport({
        file: file ?? "",
        dry_run: dryRun,
        attachments,
        inline_images: inlineImages,
        raw_email: repairText ? "repair" : "none",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["imports"] });
      onClose();
    },
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate();
  };

  return (
    <Dialog
      open={file !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t("imports.run.title", { file })}
      description={t("imports.run.intro")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="import-options" busy={start.isPending}>
            {dryRun ? t("imports.run.startDry") : t("imports.run.start")}
          </Button>
        </>
      }
    >
      <form id="import-options" onSubmit={onSubmit} className="space-y-4">
        {start.isError && <Alert>{importErrorText(start.error, t)}</Alert>}
        <CheckboxField
          label={t("imports.run.dryRun")}
          description={t("imports.run.dryRunHint")}
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        <CheckboxField
          label={t("imports.run.attachments")}
          description={t("imports.run.attachmentsHint")}
          checked={attachments}
          onChange={(e) => setAttachments(e.target.checked)}
        />
        <CheckboxField
          label={t("imports.run.inlineImages")}
          description={t("imports.run.inlineImagesHint")}
          checked={inlineImages}
          onChange={(e) => setInlineImages(e.target.checked)}
        />
        <CheckboxField
          label={t("imports.run.repairText")}
          description={t("imports.run.repairTextHint")}
          checked={repairText}
          onChange={(e) => setRepairText(e.target.checked)}
        />
      </form>
    </Dialog>
  );
}

function DeleteDialog({ file, onClose }: { file: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => importsApi.deleteFile(file ?? ""),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["imports"] });
      onClose();
    },
  });
  return (
    <Dialog
      open={file !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t("imports.files.deleteTitle")}
      description={t("imports.files.deleteBody", { file })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
            {t("imports.files.delete")}
          </Button>
        </>
      }
    >
      {remove.isError ? <Alert>{importErrorText(remove.error, t)}</Alert> : null}
    </Dialog>
  );
}

export function DumpFilesCard({ overview }: { overview: ImportsOverview | undefined }) {
  const { t } = useTranslation();
  const files = useQuery({
    queryKey: ["imports", "files"],
    queryFn: importsApi.files,
    refetchInterval: 15_000,
  });
  const [importing, setImporting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  return (
    <Card aria-labelledby="files-title" className="overflow-hidden">
      <CardHeader title={<span id="files-title">{t("imports.files.title")}</span>} />
      <CardBody className="space-y-1 pb-3 text-sm text-ink-muted">
        <p>{t("imports.files.intro")}</p>
        {overview && (
          <p className="text-xs text-ink-subtle">
            {t("imports.files.serverFolder")}{" "}
            <code className="break-all text-ink-muted">{overview.imports_dir}</code>
            {" · "}
            {t("imports.files.free", { free: formatBytes(overview.free_bytes) })}
          </p>
        )}
      </CardBody>
      <div className="relative overflow-x-auto border-t border-line">
        <table className="stack-table w-full min-w-[36rem] text-left text-sm">
          <thead className="border-b border-line bg-surface-2 text-xs text-ink-muted">
            <tr>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.files.name")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.files.size")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.files.modified")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                <span className="sr-only">{t("imports.files.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {files.data?.map((file) => (
              <tr key={file.name}>
                <td className="px-5 py-3 font-medium break-all">
                  {file.name} {file.in_use && <Badge tone="primary">{t("imports.files.inUse")}</Badge>}
                </td>
                <td className="px-5 py-3 text-ink-muted tabular-nums" data-label={t("imports.files.size")}>
                  {formatBytes(file.size)}
                </td>
                <td
                  className="px-5 py-3 text-ink-muted tabular-nums"
                  data-label={t("imports.files.modified")}
                >
                  {formatDateTime(file.modified_at)}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap max-sm:pt-2">
                  <div className="flex justify-end gap-2 max-sm:justify-start">
                    <Button
                      variant="secondary"
                      disabled={file.in_use}
                      onClick={() => setImporting(file.name)}
                      aria-label={`${t("imports.files.import")} ${file.name}`}
                    >
                      {t("imports.files.import")}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={file.in_use}
                      onClick={() => setDeleting(file.name)}
                      aria-label={`${t("imports.files.delete")} ${file.name}`}
                    >
                      {t("imports.files.delete")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {files.data?.length === 0 && (
          <p className="px-5 py-6 text-sm text-ink-muted">{t("imports.files.empty")}</p>
        )}
      </div>
      <ImportDialog key={importing ?? "none"} file={importing} onClose={() => setImporting(null)} />
      <DeleteDialog file={deleting} onClose={() => setDeleting(null)} />
    </Card>
  );
}
